import AnnouncementTemplates from "./AnnouncementTemplates";
import PageHeading from "../../components/PageHeading";
import SearchPending from "../../components/SearchPending";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { apiErrorMessage } from "../../utils/apiError";
import React, { useEffect, useState, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Plus,
  RefreshCw,
  ArrowLeft,
  Download,
  ExternalLink,
  X,
  Users,
  Loader2,
} from "lucide-react";
import { message, Select } from "antd";
import axios from "../../axios/AxiosInstance";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/reusableComponents/Dialog";
import { createListSearch } from "../../utils/listSearch";
import {
  announcementChannels as channels,
  websiteChannelId,
  applicationStatuses,
  announcementApplications,
  applicationPayload,
  safePublicationUrl,
  announcementText,
} from "../../utils/announcementManagement";

const input =
  "w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800";
const button =
  "inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50";
const primary =
  "inline-flex items-center justify-center gap-2 rounded-lg bg-blue-700 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-50";
const emptyForm = {
  title: "",
  client: "",
  contact: "",
  localisation: "",
  description: "",
  profilRecherche: "",
  jobType: "Intérim",
  salaire: "",
  baseSalary: "Heure",
  dateDebutSouhaitee: "",
  tempTravail: "",
  email: "",
  phone: "",
  informationsComplementaires: "",
  niveauQualification: "",
  experience: "",
  deplacement: "",
  presence: "",
};
const suggestions = {
  title: ["Aide-soignant / Aide-soignante", "Infirmier / Infirmière", "Agent de service hospitalier", "Auxiliaire de vie", "Préparateur de commandes", "Agent de production", "Manutentionnaire", "Assistant administratif / Assistante administrative"],
  tempTravail: ["Temps plein", "Temps partiel", "Travail de jour", "Travail de nuit", "Horaires en équipe (2 × 8)", "Horaires en équipe (3 × 8)", "Week-end"],
  niveauQualification: ["Sans diplôme requis", "CAP / BEP", "Baccalauréat", "Bac +2", "Bac +3", "Diplôme d’État requis"],
  experience: ["Débutant accepté", "Première expérience souhaitée", "1 an minimum", "2 ans minimum", "3 ans minimum"],
  deplacement: ["Aucun déplacement", "Déplacements locaux", "Déplacements régionaux", "Déplacements nationaux"],
  presence: ["Sur site", "Hybride", "Télétravail"],
};
function AnnouncementSection({ number, title, description, children }) {
  return <fieldset className="min-w-0 rounded-xl border border-slate-200 p-4">
    <legend className="px-2 text-base font-semibold text-slate-900"><span className="mr-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 text-sm text-blue-800">{number}</span>{title}</legend>
    <p className="mb-4 text-xs text-slate-500">{description}</p>
    <div className="grid gap-4 sm:grid-cols-2">{children}</div>
  </fieldset>;
}
const date = (value) =>
  value && !Number.isNaN(new Date(value).getTime())
    ? new Date(value).toLocaleString("fr-FR", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit", year: "numeric" })
    : "—";
const clientId = (row) =>
  typeof row.client === "object" ? row.client?._id : row.client;

export default function Annonces() {
  const location = useLocation();
  const navigate = useNavigate();
  const [annonces, setAnnonces] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [tab, setTab] = useState("diffusion");
  const [search, setSearch] = useState("");
  const [appliedSearch, searching] = useDebouncedValue(search);
  const loadRequest = useRef(null);
  const [stateFilter, setStateFilter] = useState("all");
  const [channelFilter, setChannelFilter] = useState("all");
  const [candidateSearch, setCandidateSearch] = useState("");
  const [candidateStatus, setCandidateStatus] = useState("all");
  const [editor, setEditor] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const renderField = (key, label, type = "text") => {
    const options = suggestions[key] || (key === "localisation" ? [...new Set(clients.map(client => client.town || client.city).filter(Boolean))] : []);
    return <label className="text-sm font-medium">{label}
      <input required={key === "title" || key === "localisation"} type={type} list={options.length ? `announcement-${key}` : undefined} placeholder={options.length ? "Choisir ou saisir…" : undefined} className={`${input} mt-2`} value={form[key] || ""} onChange={e => setForm({...form, [key]: e.target.value})} />
      {options.length > 0 && <datalist id={`announcement-${key}`}>{options.map(value => <option key={value} value={value} />)}</datalist>}
    </label>;
  };
  const renderSelect = (key, label, options) => <label className="text-sm font-medium">{label}<select className={`${input} mt-2`} value={form[key]} onChange={e => setForm({...form, [key]: e.target.value})}>{options.map(value => <option key={value}>{value}</option>)}</select></label>;
  const [cv, setCv] = useState(null);
  const [tracking, setTracking] = useState({});
  const [channelEditor, setChannelEditor] = useState(null);
  const [channelForm, setChannelForm] = useState({
    state: "À publier",
    reference: "",
    url: "",
    note: "",
  });
  const [note, setNote] = useState("");
  const [trackingBusy, setTrackingBusy] = useState(false);
  const load = async () => {
    loadRequest.current?.abort();
    const controller = new AbortController();
    loadRequest.current = controller;
    setLoading(true);
    setError("");
    const results = await Promise.allSettled([
      axios.get("annonces", { signal: controller.signal }),
      axios.get("client/freelance", { signal: controller.signal }),
      axios.get("annonces/tracking", { signal: controller.signal }),
    ]);
    if (controller.signal.aborted) return;
    if (
      results[2].status === "fulfilled" &&
      results[2].value.data &&
      typeof results[2].value.data === "object"
    )
      setTracking(results[2].value.data);
    else message.error(apiErrorMessage(results[2].reason, "Impossible de charger le suivi des annonces."));
    if (
      results[0].status === "fulfilled" &&
      Array.isArray(results[0].value.data)
    )
      setAnnonces(results[0].value.data);
    else setError(apiErrorMessage(results[0].reason, "Impossible de charger les annonces. Réessayez."));
    if (
      results[1].status === "fulfilled" &&
      Array.isArray(results[1].value.data)
    )
      setClients(results[1].value.data);
    else message.error(apiErrorMessage(results[1].reason, "Impossible de charger les clients de votre agence."));
    setLoading(false);
  };
  useEffect(() => {
    load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);
  useEffect(() => {
    const request = location.state?.announcementFromOrder;
    if (!request?.draft) return;
    setSelectedId(null);
    setForm(
      Object.fromEntries(
        Object.keys(emptyForm).map((key) => [
          key,
          request.draft[key] ?? emptyForm[key],
        ]),
      ),
    );
    setEditor({ id: null, sourceOrderId: request.sourceOrderId || "" });
    navigate(
      `${location.pathname}${location.search}${location.hash}`,
      { replace: true, state: null },
    );
  }, [
    location.hash,
    location.pathname,
    location.search,
    location.state,
    navigate,
  ]);
  const selected = annonces.find((row) => row._id === selectedId);
  const detail = tracking[selectedId] || {};
  const clientName = (row) =>
    clients.find((client) => client._id === clientId(row))?.companyName ||
    row.client?.companyName ||
    row.companyName ||
    "—";
  const saveTracking = async (path, body) => {
    if (trackingBusy) return false;
    setTrackingBusy(true);
    try {
      const { data } = await axios.post(`annonces/${selectedId}/tracking/${path}`, body);
      setTracking((current) => ({ ...current, [selectedId]: data }));
      return true;
    } catch (error) {
      message.error(apiErrorMessage(error, "Enregistrement impossible. Aucun changement sauvegardé."));
      return false;
    } finally {
      setTrackingBusy(false);
    }
  };
  const update = (row) =>
    setAnnonces((current) =>
      current.map((item) => (item._id === row._id ? row : item)),
    );
  const edit = (row, duplicate = false) => {
    setForm({
      ...emptyForm,
      ...row,
      client: clientId(row || {}) || "",
      title: duplicate ? `${row.title} (copie)` : row?.title || "",
    });
    setEditor({ id: duplicate ? null : row?._id });
  };
  const saveAnnouncement = async (event) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const payload = Object.fromEntries(
        Object.keys(emptyForm).map((key) => [key, form[key] ?? ""]),
      );
      const { data } = editor.id
        ? await axios.patch(`annonces/${editor.id}`, payload)
        : await axios.post("annonces", payload);
      if (!data?._id) throw new Error("Réponse invalide");
      if (editor.id) update(data);
      else setAnnonces((current) => [data, ...current]);
      setEditor(null);
      setSelectedId(data._id);
      setTab("diffusion");
      message.success("Annonce enregistrée.");
    } catch (error) {
      message.error(
        apiErrorMessage(error, "Impossible d’enregistrer l’annonce. Vos saisies sont conservées."),
      );
    } finally {
      setBusy(false);
    }
  };
  const activate = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`annonces/activate/${selected._id}`, {
        status: !selected.isActive,
      });
      if (!data?._id || Boolean(data.isActive) === Boolean(selected.isActive))
        throw new Error("Statut non confirmé");
      update({ ...selected, ...data });
      message.success(
        data.isActive
          ? "Diffusion sur le site activée."
          : "Diffusion sur le site suspendue.",
      );
    } catch (error) {
      message.error(
        apiErrorMessage(error, "La mise à jour de la diffusion n’a pas été confirmée. Réessayez."),
      );
    } finally {
      setBusy(false);
    }
  };
  const archive = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const archivee = !selected.archivee;
      const { data } = await axios.patch(`annonces/${selected._id}`, {
        archivee,
        ...(archivee ? { isActive: false } : {}),
      });
      if (!data?._id || Boolean(data.archivee) === Boolean(selected.archivee))
        throw new Error("Archivage non confirmé");
      if (archivee && data.isActive)
        throw new Error("La diffusion est toujours active");
      update({ ...selected, ...data });
      message.success(
        data.archivee ? "Annonce archivée." : "Annonce restaurée.",
      );
    } catch (error) {
      message.error(apiErrorMessage(error, "L’archivage n’a pas été confirmé par le serveur."));
    } finally {
      setBusy(false);
    }
  };
  const changeCandidateStatus = async (candidate, status) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await axios.patch(`annonces/${selected._id}`, {
        candidats: applicationPayload(selected, candidate._id, status),
      });
      if (
        !data?._id ||
        !announcementApplications(data).some(
          (row) =>
            String(row._id) === String(candidate._id) && row.status === status,
        )
      )
        throw new Error("Statut non confirmé");
      update({ ...selected, ...data });
      message.success("Statut du candidat enregistré.");
    } catch (error) {
      message.error(apiErrorMessage(error, "Le changement de statut n’a pas été confirmé."));
    } finally {
      setBusy(false);
    }
  };
  const exportOffer = () => {
    const url = URL.createObjectURL(
      new Blob([announcementText(selected)], {
        type: "text/plain;charset=utf-8",
      }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = `Annonce-${selected._id}.txt`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const applications = selected ? announcementApplications(selected) : [];
  const matchesCandidate = createListSearch(candidateSearch, [
    "firstname",
    "lastname",
    "phone",
    "email",
    "town",
    "qualifications",
  ]);
  const visibleCandidates = applications.filter(
    (row) =>
      (candidateStatus === "all" || row.status === candidateStatus) &&
      matchesCandidate(row),
  );
  const indexedAnnouncements = useMemo(
    () =>
      annonces.map((row) => ({
        ...row,
        searchClient:
          clients.find((client) => client._id === clientId(row))?.companyName ||
          row.client?.companyName ||
          row.companyName ||
          "",
      })),
    [annonces, clients],
  );
  const matches = createListSearch(appliedSearch, [
    "title",
    "description",
    "profilRecherche",
    "localisation",
    "town",
    "jobType",
    "client.companyName",
    "searchClient",
    "_id",
  ]);
  const visible = indexedAnnouncements.filter(
    (row) =>
      matches(row) &&
      (stateFilter === "all" ||
        (stateFilter === "archived"
          ? row.archivee
          : !row.archivee &&
            (stateFilter === "active" ? row.isActive : !row.isActive))) &&
      (channelFilter === "all" ||
        (channelFilter === websiteChannelId
          ? row.isActive
          : tracking[row._id]?.channels?.[channelFilter]?.state === "Publié")),
  );
  return (
    <main className="annonces-page">
      <header className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div>
            <PageHeading
              icon="announcements"
              title={<> Annonces </>}
              description={
                <> Une annonce, quatre canaux et un suivi des candidatures. </>
              }
            />
          </div>
        </div>
        <div className="flex gap-2">
          <button className={`${button} inline-flex items-center gap-2`} onClick={load} disabled={loading || busy}>
            {loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
            {loading ? "Actualisation…" : "Actualiser"}
          </button>
          <button className={primary} onClick={() => edit(null)}>
            <Plus size={17} />
            Nouvelle annonce
          </button>
        </div>
      </header>
      <p className="mb-6 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-slate-600">
        Le site : publication via l’API existante. Mon Intérim, Pôle emploi et
        HelloWork : suivi manuel partagé avec l’agence ; publication et récupération
        automatiques des candidatures en attente des connexions serveur.
      </p>
      {error && (
        <p role="alert" className="mb-4 text-red-700">
          {error}
        </p>
      )}
      {selected ? (
        <>
          <button className={button} onClick={() => setSelectedId(null)}>
            <ArrowLeft size={16} />
            Toutes les annonces
          </button>
          <section className="my-5 rounded-xl border border-slate-200 bg-white p-5">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">
                  {selected.title}
                </h2>
                <p className="mt-2 text-sm text-slate-500">
                  {clientName(selected)} ·{" "}
                  {selected.localisation || selected.town || "Lieu à préciser"}{" "}
                  · {selected.jobType || "Contrat à préciser"}
                </p>
                <p className="mt-1 text-xs text-slate-400">
                  Référence {selected._id} · {applications.length}{" "}
                  candidature(s)
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button className={button} onClick={() => edit(selected)}>
                  Modifier
                </button>
                <button className={button} onClick={() => edit(selected, true)}>
                  Dupliquer
                </button>
                <button
                  className={button}
                  disabled={busy}
                  onClick={archive}
                >
                  {selected.archivee ? "Restaurer" : "Archiver"}
                </button>
              </div>
            </div>
          </section>
          <nav
            aria-label="Gestion de l’annonce"
            className="mb-5 flex flex-wrap gap-2"
          >
            {[
              ["diffusion", "Diffusion"],
              ["content", "Contenu"],
              ["candidates", `Candidatures (${applications.length})`],
              ["follow", "Suivi"],
            ].map(([id, label]) => (
              <button
                key={id}
                className={`${button} ${tab === id ? "bg-blue-50 text-blue-700" : ""}`}
                aria-pressed={tab === id}
                onClick={() => setTab(id)}
              >
                {label}
              </button>
            ))}
          </nav>
          {tab === "diffusion" && (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {channels.map((channel) => {
                const publication = detail.channels?.[channel.id] || {};
                const automatic = channel.id === websiteChannelId;
                return (
                  <section
                    key={channel.id}
                    className="rounded-xl border border-slate-200 bg-white p-5"
                  >
                    <h3 className="font-bold text-slate-900">
                      {channel.label}
                    </h3>
                    <p className="mt-1 text-xs text-slate-500">
                      {automatic
                        ? "Publication sur le site"
                        : "Suivi manuel"}
                    </p>
                    <p
                      className={`my-5 text-sm font-semibold ${(automatic ? selected.isActive : publication.state === "Publié") ? "text-emerald-700" : "text-slate-600"}`}
                    >
                      {automatic
                        ? selected.isActive
                          ? "Diffusion activée"
                          : "Diffusion suspendue"
                        : `${publication.state || "À publier"}${publication.state ? " (déclaré)" : ""}`}
                    </p>
                    {!automatic && (
                      <>
                        <p className="text-xs text-slate-500">
                          Référence : {publication.reference || "—"}
                        </p>
                        <p className="mt-1 text-xs text-slate-500">
                          Mis à jour : {date(publication.savedAt)}
                        </p>
                        {publication.note && (
                          <p className="mt-3 whitespace-pre-wrap text-sm text-slate-600">
                            {publication.note}
                          </p>
                        )}
                      </>
                    )}
                    <div className="mt-5 flex flex-wrap gap-2">
                      {automatic ? (
                        <button
                          disabled={busy || Boolean(selected.archivee)}
                          className={primary}
                          onClick={activate}
                        >
                          {selected.isActive
                            ? "Suspendre la diffusion"
                            : "Activer la diffusion"}
                        </button>
                      ) : (
                        <>
                          <button
                            className={button}
                            onClick={() => {
                              setChannelEditor(channel);
                              setChannelForm({
                                state: "À publier",
                                reference: "",
                                url: "",
                                note: "",
                                ...publication,
                              });
                            }}
                          >
                            Mettre à jour le suivi
                          </button>
                          {safePublicationUrl(publication.url) && (
                            <a
                              className={button}
                              href={safePublicationUrl(publication.url)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <ExternalLink size={15} />
                              Voir l’offre
                            </a>
                          )}
                          <button className={button} onClick={exportOffer}>
                            <Download size={15} />
                            Exporter le contenu
                          </button>
                        </>
                      )}
                    </div>
                  </section>
                );
              })}
            </div>
          )}
          {tab === "content" && (
            <section className="rounded-xl border border-slate-200 bg-white p-5">
              <div className="mb-4 flex justify-between gap-3">
                <h3 className="font-semibold">Contenu de l’annonce</h3>
                <button className={button} onClick={exportOffer}>
                  <Download size={15} />
                  Exporter
                </button>
              </div>
              <p className="whitespace-pre-wrap text-sm leading-7 text-slate-700">
                {announcementText(selected)}
              </p>
            </section>
          )}
          {tab === "candidates" && (
            <section>
              <div className="mb-4 flex flex-wrap gap-3">
                <input
                  className={`${input} max-w-md`}
                  aria-label="Rechercher une candidature"
                  placeholder="Nom, téléphone, email, ville…"
                  value={candidateSearch}
                  onChange={(e) => setCandidateSearch(e.target.value)}
                />
                <select
                  className={`${input} max-w-56`}
                  aria-label="Filtrer les candidatures"
                  value={candidateStatus}
                  onChange={(e) => setCandidateStatus(e.target.value)}
                >
                  <option value="all">Tous les statuts</option>
                  {applicationStatuses.map((status) => (
                    <option key={status}>{status}</option>
                  ))}
                </select>
              </div>
              <p className="mb-4 text-xs text-slate-500">
                Candidatures reçues par le site. Les candidatures Mon Intérim, Pôle
                emploi et HelloWork ne sont pas synchronisées.
              </p>
              <div className="rounded-xl border border-slate-200 bg-white">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr>
                      {["Candidat", "Contact", "Origine", "Statut", "CV"].map(
                        (label) => (
                          <th key={label}>{label}</th>
                        ),
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleCandidates.map((candidate) => (
                      <tr key={candidate._id}>
                        <td className="font-semibold">
                          {candidate.firstname} {candidate.lastname}
                        </td>
                        <td>
                          <div>
                            {candidate.phone && (
                              <a href={`tel:${candidate.phone}`}>
                                {candidate.phone}
                              </a>
                            )}
                          </div>
                          {candidate.email && (
                            <a href={`mailto:${candidate.email}`}>
                              {candidate.email}
                            </a>
                          )}
                        </td>
                        <td>{candidate.source || "Origine non fournie"}</td>
                        <td>
                          <select
                            className={input}
                            value={candidate.status}
                            disabled={busy}
                            aria-label={`Statut de ${candidate.firstname} ${candidate.lastname}`}
                            onChange={(e) =>
                              changeCandidateStatus(candidate, e.target.value)
                            }
                          >
                            {[
                              ...new Set([
                                ...applicationStatuses,
                                candidate.status,
                              ]),
                            ].map((status) => (
                              <option key={status}>{status}</option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <button
                            className={button}
                            disabled={busy}
                            onClick={async () => {
                              setBusy(true);
                              try {
                                const { data } = await axios.get(
                                  `cv/${candidate._id}`,
                                );
                                if (!data?.data) throw new Error();
                                setCv({
                                  name: `${candidate.firstname} ${candidate.lastname}`,
                                  data: data.data,
                                });
                              } catch (error) {
                                message.error(apiErrorMessage(error, "CV indisponible."));
                              } finally {
                                setBusy(false);
                              }
                            }}
                          >
                            Consulter
                          </button>
                        </td>
                      </tr>
                    ))}
                    {!visibleCandidates.length && (
                      <tr>
                        <td
                          colSpan={5}
                          className="p-8 text-center text-slate-500"
                        >
                          Aucune candidature pour ces critères.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          )}
          {tab === "follow" && (
            <section className="rounded-xl border border-slate-200 bg-white p-5">
              <h3 className="font-semibold">Notes de suivi</h3>
              <p className="mt-1 text-xs text-slate-500">
                Notes partagées avec l’équipe de l’agence.
              </p>
              <form
                className="my-4"
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (!note.trim()) return;
                  if (await saveTracking("note", { text: note.trim() })) {
                    setNote("");
                    message.success("Note enregistrée.");
                  }
                }}
              >
                <textarea
                  aria-label="Note de suivi de l’annonce"
                  required
                  className={input}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Retour du client, relance, modification à prévoir…"
                />
                <button className={`${primary} mt-2`} type="submit">
                  Enregistrer la note
                </button>
              </form>
              {(detail.notes || []).map((entry, index) => (
                <article key={index} className="border-t border-slate-100 py-4">
                  <p className="whitespace-pre-wrap text-sm">{entry.text}</p>
                  <p className="mt-2 text-xs text-slate-500">
                    {entry.author} · {date(entry.savedAt)}
                  </p>
                </article>
              ))}
            </section>
          )}
        </>
      ) : (
        <>
          <div className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {channels.map((channel) => (
              <div
                key={channel.id}
                className="rounded-xl border border-slate-200 bg-white p-4"
              >
                <p className="text-sm text-slate-500">{channel.label}</p>
                <p className="mt-2 text-2xl font-bold text-slate-900">
                  {
                    annonces.filter((row) =>
                      channel.id === websiteChannelId
                        ? row.isActive
                        : tracking[row._id]?.channels?.[channel.id]?.state ===
                          "Publié",
                    ).length
                  }
                </p>
                <p className="text-xs text-slate-500">
                  {channel.id === websiteChannelId
                    ? "diffusion activée"
                    : "publication déclarée manuellement"}
                </p>
              </div>
            ))}
          </div>
          <div className="mb-5 flex flex-wrap gap-3">
            <input
              className={`${input} min-w-0 flex-1`}
              aria-label="Rechercher une annonce"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Poste, client, ville, contrat, référence…"
            />
            <SearchPending pending={searching} />
            <select
              className={`${input} sm:max-w-52`}
              aria-label="État de l’annonce"
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
            >
              <option value="all">Tous les états</option>
              <option value="active">Diffusion activée</option>
              <option value="inactive">Diffusion suspendue</option>
              <option value="archived">Archivées</option>
            </select>
            <select
              className={`${input} sm:max-w-52`}
              aria-label="Canal de publication"
              value={channelFilter}
              onChange={(e) => setChannelFilter(e.target.value)}
            >
              <option value="all">Tous les canaux</option>
              {channels.map((channel) => (
                <option key={channel.id} value={channel.id}>
                  {channel.label}
                </option>
              ))}
            </select>
          </div>
          {loading ? (
            <p role="status" className="flex items-center justify-center gap-2 py-10 text-center text-slate-500">
              <Loader2 className="animate-spin" size={18} />
              Chargement des annonces…
            </p>
          ) : (
            <div className="rounded-xl border border-slate-200 bg-white">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr>
                    {[
                      "Annonce / client",
                      "Contrat / lieu",
                      ...channels.map((channel) => channel.label),
                      "Candidatures",
                      "Action",
                    ].map((label) => (
                      <th key={label}>{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visible.map((row) => (
                    <tr key={row._id}>
                      <td>
                        <p className="font-semibold text-slate-900">
                          {row.title}
                        </p>
                        <p className="mt-1 text-xs text-slate-500">
                          {clientName(row)}
                          {row.archivee && " · Archivée"}
                        </p>
                      </td>
                      <td>
                        {row.jobType}
                        <p className="text-xs text-slate-500">
                          {row.localisation || row.town || "—"}
                        </p>
                      </td>
                      {channels.map((channel) => (
                        <td key={channel.id}>
                          {channel.id === websiteChannelId ? (
                            <>
                              <p>{row.isActive ? "Diffusion activée" : "Suspendue"}</p>
                              <span className="text-xs text-slate-400">Site public</span>
                            </>
                          ) : (
                            <>
                              <p>
                                {tracking[row._id]?.channels?.[channel.id]?.state ||
                                  "À publier"}
                              </p>
                              <span className="text-xs text-slate-400">Suivi manuel</span>
                            </>
                          )}
                        </td>
                      ))}
                      <td>
                        <Users size={15} className="inline mr-2" />
                        {announcementApplications(row).length}
                      </td>
                      <td>
                        <button
                          className={button}
                          onClick={() => {
                            setSelectedId(row._id);
                            setTab("diffusion");
                            setNote("");
                            setCandidateSearch("");
                            setCandidateStatus("all");
                          }}
                        >
                          Gérer
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!visible.length && (
                    <tr>
                      <td
                        colSpan={8}
                        className="p-10 text-center text-slate-500"
                      >
                        Aucune annonce pour ces critères.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      <Dialog
        open={Boolean(editor)}
        onOpenChange={(open) => {
          if (!open && !busy) setEditor(null);
        }}
      >
        <DialogContent className="w-[calc(100%-2rem)] max-w-3xl overflow-hidden rounded-xl bg-white">
          <div>
            <div className="sticky top-0 z-20 flex items-center justify-between gap-4 border-b border-slate-200 bg-white px-6 py-4">
              <DialogHeader>
                <DialogTitle>
                  {editor?.id ? "Modifier l’annonce" : "Nouvelle annonce"}
                </DialogTitle>
              </DialogHeader>
              <button
                className="shrink-0 rounded-lg p-2 text-slate-500 hover:bg-slate-100"
                aria-label="Fermer"
                onClick={() => {
                  if (!busy) setEditor(null);
                }}
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={saveAnnouncement} className="space-y-5 p-6">
              {editor?.sourceOrderId && (
                <p
                  role="status"
                  className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900"
                >
                  Formulaire prérempli depuis la commande{" "}
                  <strong>{editor.sourceOrderId}</strong>. Vérifiez les
                  informations : aucune annonce ne sera créée avant votre
                  enregistrement.
                </p>
              )}
              <AnnouncementSection number="1" title="Client et contact" description="Sélectionnez le client : ses coordonnées sont reprises automatiquement.">
                <label className="text-sm font-medium">
                  Client
                  <Select
                    className="mt-2 w-full"
                    getPopupContainer={(trigger) => trigger.parentElement}
                    showSearch
                    optionFilterProp="label"
                    value={form.client || undefined}
                    onChange={(value) => {
                      const client = clients.find((row) => row._id === value);
                      setForm({
                        ...form,
                        client: value,
                        contact: "",
                        email: client?.contactEMail || "",
                        phone: client?.contactPhone || "",
                        localisation: client?.town || client?.city || "",
                      });
                    }}
                    options={clients.map((client) => ({
                      value: client._id,
                      label: client.companyName,
                    }))}
                    placeholder="Choisir le client"
                  />
                </label>
                <label className="text-sm font-medium">
                  Contact client
                  <select
                    className={`${input} mt-2`}
                    value={form.contact || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      const contact = (
                        clients.find((row) => row._id === form.client)
                          ?.contacts || []
                      ).find(
                        (row) =>
                          String(
                            row._id || `${row.firstname} ${row.lastname}`,
                          ) === value,
                      );
                      setForm({
                        ...form,
                        contact: value,
                        email: contact?.email || form.email,
                        phone: contact?.phone || contact?.mobile || form.phone,
                      });
                    }}
                  >
                    <option value="">Choisir un contact</option>
                    {(
                      clients.find((row) => row._id === form.client)
                        ?.contacts || []
                    ).map((contact, index) => (
                      <option
                        key={contact._id || index}
                        value={
                          contact._id ||
                          `${contact.firstname} ${contact.lastname}`
                        }
                      >
                        {[contact.firstname, contact.lastname]
                          .filter(Boolean)
                          .join(" ")}
                      </option>
                    ))}
                  </select>
                </label>

                {renderField("email", "Email de contact", "email")}
                {renderField("phone", "Téléphone de contact", "tel")}
              </AnnouncementSection>
              <AnnouncementSection number="2" title="Métier et profil" description="Choisissez une proposition ou saisissez votre propre intitulé.">
                {renderField("title", "Intitulé du poste")}
                {renderField("localisation", "Lieu de travail")}
                {renderField("niveauQualification", "Niveau de qualification")}
                {renderField("experience", "Expérience souhaitée")}
              </AnnouncementSection>
              <AnnouncementSection number="3" title="Contrat et conditions" description="Précisez la date de démarrage, les horaires et la rémunération.">
                {renderSelect("jobType", "Type de contrat", ["Intérim", "CDD", "CDI", "Vacation", "Stage", "Apprentissage", "Freelance"])}
                {renderField("dateDebutSouhaitee", "Début souhaité", "date")}
                {renderField("tempTravail", "Temps de travail / horaires")}
                {renderField("salaire", "Rémunération proposée")}
                {renderSelect("baseSalary", "Base de rémunération", ["Heure", "Jour", "Semaine", "Mois", "An"])}
                {renderField("presence", "Présence / télétravail")}
                {renderField("deplacement", "Déplacements")}
              </AnnouncementSection>
              <AnnouncementSection number="4" title="Contenu de l’annonce" description="Ajoutez les missions et les compétences attendues. Les propositions restent modifiables.">
                {[
                  ["description", "Description et missions", "Vous interviendrez au sein de l’équipe de notre client pour assurer les missions liées au poste.\nMissions principales :\n• …\n• …"],
                  ["profilRecherche", "Profil recherché", "Nous recherchons une personne rigoureuse, ponctuelle et appréciant le travail en équipe.\nCompétences et diplômes requis : …"],
                  ["informationsComplementaires", "Informations complémentaires", "Poste ouvert aux personnes en situation de handicap. Les conditions de la mission seront précisées lors de l’entretien."],
                ].map(([key, label, proposal]) => <div key={key} className="text-sm font-medium sm:col-span-2">
                  <div className="flex flex-wrap items-center justify-between gap-2"><label htmlFor={`announcement-${key}`}>{label}</label></div>
                  <AnnouncementTemplates field={key} proposal={proposal} onInsert={text => setForm(current => ({ ...current, [key]: current[key] ? `${current[key]}\n\n${text}` : text }))} />
                  <textarea id={`announcement-${key}`} required={key !== "informationsComplementaires"} rows={4} className={`${input} mt-2`} value={form[key] || ""} onChange={e => setForm({...form, [key]: e.target.value})} />
                </div>)}
              </AnnouncementSection>
              <p className="text-xs text-slate-500">
                L’enregistrement ne confirme aucune publication sur Mon Intérim,
                Pôle emploi ou HelloWork.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  className={button}
                  disabled={busy}
                  onClick={() => setEditor(null)}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className={primary}
                  disabled={busy || !form.client}
                >
                  {busy ? "Enregistrement…" : "Enregistrer l’annonce"}
                </button>
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog
        open={Boolean(channelEditor)}
        onOpenChange={(open) => {
          if (!open) setChannelEditor(null);
        }}
      >
        <DialogContent className="bg-white">
          <form
            className="space-y-4 p-6"
            onSubmit={async (e) => {
              e.preventDefault();
              if (channelForm.url && !safePublicationUrl(channelForm.url)) {
                message.error("Indiquez une URL http ou https valide.");
                return;
              }
              if (
                await saveTracking("channel", {
                  channel: channelEditor.id,
                  state: channelForm.state,
                  reference: channelForm.reference,
                  url: channelForm.url,
                  note: channelForm.note,
                })
              ) {
                setChannelEditor(null);
                message.success("Suivi enregistré.");
              }
            }}
          >
            <DialogHeader>
              <DialogTitle>Suivi {channelEditor?.label}</DialogTitle>
            </DialogHeader>
            <p className="text-xs text-slate-500">
              Saisissez les informations après avoir effectué l’action sur la
              plateforme. Ce formulaire ne publie ni ne suspend l’offre.
            </p>
            <label className="block text-sm">
              État déclaré
              <select
                className={`${input} mt-1`}
                value={channelForm.state}
                onChange={(e) =>
                  setChannelForm({ ...channelForm, state: e.target.value })
                }
              >
                {["À publier", "Publié", "Suspendu", "Clôturé"].map((value) => (
                  <option key={value}>{value}</option>
                ))}
              </select>
            </label>
            {[
              ["reference", "Référence sur la plateforme"],
              ["url", "Lien public de l’offre"],
              ["note", "Commentaire"],
            ].map(([key, label]) => (
              <label className="block text-sm" key={key}>
                {label}
                <input
                  className={`${input} mt-1`}
                  type={key === "url" ? "url" : "text"}
                  value={channelForm[key]}
                  onChange={(e) =>
                    setChannelForm({ ...channelForm, [key]: e.target.value })
                  }
                />
              </label>
            ))}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className={button}
                onClick={() => setChannelEditor(null)}
              >
                Annuler
              </button>
              <button className={primary} type="submit">
                Enregistrer le suivi
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog
        open={Boolean(cv)}
        onOpenChange={(open) => {
          if (!open) setCv(null);
        }}
      >
        <DialogContent className="max-w-4xl bg-white p-5">
          <DialogHeader>
            <DialogTitle>CV · {cv?.name}</DialogTitle>
          </DialogHeader>
          <button
            className="absolute right-4 top-4"
            aria-label="Fermer le CV"
            onClick={() => setCv(null)}
          >
            <X size={20} />
          </button>
          {cv && (
            <iframe
              title={`CV de ${cv.name}`}
              className="mt-4 h-[65vh] w-full"
              src={`data:application/pdf;base64,${cv.data}`}
            />
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
}
